import { Component, Host, h, Prop } from '@stencil/core';

const defaultColor: string = 'var(--aha-blue-500)';
const defaultStroke: string = 'calc(var(--aha-spinner--size) * 0.1)';
const defaultTrackColor: string = 'var(--aha-blue-500)';

export interface AhaSpinner extends HTMLAhaSpinnerElement {}

/**
 * @slot DEFAULT - Contents that appear inside spinner. Useful for things like progress information.
 */
@Component({
  tag: 'aha-spinner',
  styleUrl: 'aha-spinner.css',
  shadow: true,
})
export class AhaSpinner {
  /** Pauses a spinner when set to `true`, animates when set to `false`. */
  @Prop({ reflect: true }) paused: boolean = false;

  /** Sets the size of the spinner. By default it is 1.5 times the width of the `x` character in whatever font is applied. This way the size is relative to the surrounding text. */
  @Prop({ reflect: true }) size: string = '1.5ex';

  /** Sets the stroke of the spinner. By default it is the size of the spinner * 0.1 */
  @Prop({ reflect: true }) stroke: string = 'auto';

  get strokeValue() {
    return this.stroke === 'auto' ? defaultStroke : this.stroke;
  }

  /** Sets the color of the spinner. */
  @Prop({ reflect: true }) color: string = 'auto';

  // Use default value if external-facing attribute is 'auto'.
  get colorValue() {
    return this.color === 'auto' ? defaultColor : this.color;
  }

  /** Sets the duration of the spinner animation (in seconds for 1 full loop of the animation). */
  @Prop({ reflect: true }) duration: number = 0.4;

  /** Sets the opacity of the spinner. */
  @Prop({ reflect: true }) opacity: number = 0.5;

  /** Adds a "track" to the spinner when set to true. */
  @Prop({ reflect: true }) track: boolean = false;

  /** Sets the color of the track. */
  @Prop({ reflect: true }) trackColor: string = 'auto';
  get trackColorValue() {
    return this.trackColor === 'auto' ? defaultTrackColor : this.trackColor;
  }

  /** Sets the opacity of the track. */
  @Prop({ reflect: true }) trackOpacity: number = 0.2;

  render() {
    const baseStyle = {
      borderWidth: `${this.strokeValue}`,
    };
    const trackStyle = Object.assign({}, baseStyle, {
      opacity: `${this.trackOpacity}`,
      borderColor: this.trackColorValue,
    });

    const spinnerStyle = Object.assign({}, baseStyle, {
      opacity: `${this.opacity}`,
      borderColor: `${this.colorValue} ${this.colorValue} transparent transparent`,
      animation: this.paused ? undefined : `aha-spinner ${this.duration}s linear infinite`,
    });

    return (
      <Host
        style={{ '--aha-spinner--size': `${this.size}` }}
        aria-hidden={this.paused ? true : false}
        aria-busy={this.paused ? false : true}
        aria-valuetext="Loading"
        role="progressbar"
      >
        <div id="track" style={trackStyle} hidden={!this.track}></div>
        <div id="spinner" style={spinnerStyle}></div>
        <div id="content">
          <slot></slot>
        </div>
      </Host>
    );
  }
}
